import React, { useState, useEffect, useCallback } from 'react';
import { Button, Message, Header, Progress, Icon } from 'semantic-ui-react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useBluetooth } from '../../contexts/BluetoothContext';
import CalibrationToast from '../CalibrationToast/CalibrationToast';
import './BluetoothSetupFlow.css';

interface BluetoothSetupFlowProps extends WithTranslation {
  onReady?: () => void;
  onCalibrationComplete?: (weight: number, addedWeights: number) => void;
  showReadyStep?: boolean;
  readyButtonText?: string;
  readyDescription?: string;
  testMode?: boolean; // New prop for test mode
}

const BluetoothSetupFlow: React.FC<BluetoothSetupFlowProps> = ({ 
  t, 
  onReady,
  onCalibrationComplete,
  showReadyStep = true,
  readyButtonText = "Start Workout",
  readyDescription = "Your device is connected and calibrated",
  testMode = false
}) => {
  const {
    isConnected,
    isCalibrated,
    isCalibrating,
    userWeight,
    addedWeights,
    connectionError,
    calibrationError,
    connect,
    startCalibration,
  } = useBluetooth();

  const [calibrationProgress, setCalibrationProgress] = useState(0);
  const [calibrationComplete, setCalibrationComplete] = useState(false);
  const [calibrationDataPoints, setCalibrationDataPoints] = useState(0);
  const [totalDataPoints] = useState(20); // Increased from 10 to 20
  const [isWorkoutStarted, setIsWorkoutStarted] = useState(false);

  // Test mode state
  const [testConnected, setTestConnected] = useState(false);
  const [testCalibrated, setTestCalibrated] = useState(false);
  const [testCalibrating, setTestCalibrating] = useState(false);
  const [testUserWeight, setTestUserWeight] = useState(70);
  const [testAddedWeights, setTestAddedWeights] = useState(0);
  const [testConnectionError, setTestConnectionError] = useState<string | null>(null);
  const [testCalibrationError, setTestCalibrationError] = useState<string | null>(null);

  // Toast notification state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [toastTitle, setToastTitle] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  // Get current state (test mode or real)
  const getCurrentState = () => {
    if (testMode) {
      return {
        isConnected: testConnected,
        isCalibrated: testCalibrated,
        isCalibrating: testCalibrating,
        userWeight: testUserWeight,
        addedWeights: testAddedWeights,
        connectionError: testConnectionError,
        calibrationError: testCalibrationError,
      };
    }
    return {
      isConnected,
      isCalibrated,
      isCalibrating,
      userWeight,
      addedWeights,
      connectionError,
      calibrationError,
    };
  };

  const currentState = getCurrentState();

  // Determine current step
  const getCurrentStep = () => {
    if (!currentState.isConnected) return 'connect';
    if (currentState.isConnected && !currentState.isCalibrated) return 'calibrate';
    if (currentState.isConnected && currentState.isCalibrated && !isWorkoutStarted) {
      // Always show ready step when calibrated, regardless of showReadyStep prop
      return 'ready';
    }
    if (currentState.isConnected && currentState.isCalibrated && isWorkoutStarted) return 'complete';
    return 'complete';
  };

  const currentStep = getCurrentStep();
  


  // Toast notification functions
  const showToast = useCallback((type: 'success' | 'error', title: string, message: string) => {
    setToastType(type);
    setToastTitle(title);
    setToastMessage(message);
    setToastVisible(true);
  }, []);

  const hideToast = useCallback(() => {
    setToastVisible(false);
  }, []);

  const handleCalibrationComplete = useCallback((weight: number, addedWeights: number) => {
    console.log('BluetoothSetupFlow: Calibration complete:', { weight, addedWeights });
    
    if (onCalibrationComplete) {
      onCalibrationComplete(weight, addedWeights);
    }
  }, [onCalibrationComplete]);

  // Handle calibration progress
  useEffect(() => {
    const calibrating = testMode ? testCalibrating : isCalibrating;
    if (calibrating) {
      setCalibrationProgress(0);
      setCalibrationComplete(false);
      setCalibrationDataPoints(0);
      
      // Simulate progress during calibration
      const interval = setInterval(() => {
        setCalibrationDataPoints(prev => {
          const newPoints = prev + 1;
          const progress = (newPoints / totalDataPoints) * 100;
          setCalibrationProgress(progress);
          
          if (newPoints >= totalDataPoints) {
            clearInterval(interval);
            setCalibrationProgress(100);
          }
          return newPoints;
        });
      }, 400);
      
      return () => clearInterval(interval);
    }
  }, [testMode, testCalibrating, isCalibrating, totalDataPoints]);

  // Handle calibration completion - show success message when calibration is done
  useEffect(() => {
    const calibrated = testMode ? testCalibrated : isCalibrated;
    const calibrating = testMode ? testCalibrating : isCalibrating;
    if (calibrated && !calibrating) {
      console.log('BluetoothSetupFlow: Calibration completed, setting calibrationComplete to true');
      setCalibrationComplete(true);
      setCalibrationProgress(100);
      
      // Show success toast
      const weight = testMode ? testUserWeight : userWeight;
      showToast('success', 'Calibration Successful!', `Your device is calibrated at ${weight}kg. You can now start your workout.`);
      
      // Don't call handleCalibrationComplete immediately - let user see success message first
    }
  }, [testMode, testCalibrated, isCalibrated, testCalibrating, isCalibrating, testUserWeight, userWeight, showToast]);

  // Handle calibration errors
  useEffect(() => {
    const error = testMode ? testCalibrationError : calibrationError;
    if (error) {
      showToast('error', 'Calibration Failed', error);
    }
  }, [testMode, testCalibrationError, calibrationError, showToast]);

  // Test mode handlers
  const handleTestConnect = () => {
    setTestConnected(true);
    setTestConnectionError(null);
  };

  const handleTestStartCalibration = () => {
    setTestCalibrating(true);
    setTestCalibrationError(null);
    setCalibrationComplete(false);
    setCalibrationDataPoints(0);
    setCalibrationProgress(0);
    
    // Simulate calibration completion after progress
    setTimeout(() => {
      setTestCalibrating(false);
      setTestCalibrated(true);
    }, 8000); // 8 seconds to match progress animation
  };


  // Real mode handlers
  const handleConnect = async () => {
    if (testMode) {
      handleTestConnect();
      return;
    }
    
    try {
      await connect();
    } catch (error) {
      console.error('Failed to connect:', error);
    }
  };

  const handleStartCalibration = () => {
    if (testMode) {
      handleTestStartCalibration();
      return;
    }
    
    console.log('BluetoothSetupFlow: Starting calibration...');
    console.log('BluetoothSetupFlow: Current state before calibration:', {
      isConnected: currentState.isConnected,
      isCalibrated: currentState.isCalibrated,
      isCalibrating: currentState.isCalibrating,
      calibrationComplete,
      startCalibration: typeof startCalibration
    });
    
    setCalibrationComplete(false);
    setCalibrationDataPoints(0);
    setCalibrationProgress(0);
    startCalibration();
  };

  const handleStartWorkout = useCallback(() => {
    console.log('BluetoothSetupFlow: Starting workout...');
    console.log('BluetoothSetupFlow: handleStartWorkout called - this should only happen when user clicks button');
    setIsWorkoutStarted(true);
    
    // Call calibration complete callback when user starts workout
    const weight = testMode ? testUserWeight : userWeight;
    const addedWeight = testMode ? testAddedWeights : addedWeights;
    handleCalibrationComplete(weight, addedWeight);
    
    if (onReady) {
      onReady();
    }
  }, [testMode, testUserWeight, testAddedWeights, userWeight, addedWeights, handleCalibrationComplete, onReady]);

  // Auto-start workout when calibration is complete
  useEffect(() => {
    if (currentState.isConnected && currentState.isCalibrated && !isWorkoutStarted && currentStep === 'ready') {
      console.log('BluetoothSetupFlow: Auto-starting workout after calibration completion');
      // Small delay to ensure UI is ready
      setTimeout(() => {
        handleStartWorkout();
      }, 1000);
    }
  }, [currentState.isConnected, currentState.isCalibrated, isWorkoutStarted, currentStep, handleStartWorkout]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 'connect':
        return (
          <div className="step-content">
            <div className="step-image">
              <Icon name="bluetooth" size="massive" style={{ color: '#FFFFFF' }} />
            </div>
            <p className="step-description">
              Make sure your Entralpi device is powered on and within range. 
              Click the button below to establish a Bluetooth connection.
            </p>
            <Button 
              primary 
              size="large"
              onClick={handleConnect}
              loading={false}
              className="step-button"
            >
              <Icon name="bluetooth" />
              {t('setup.bluetooth.connect_device')}
            </Button>
            {currentState.connectionError && (
              <Message negative className="error-message">
                <Message.Header>{t('setup.bluetooth.connection_error')}</Message.Header>
                <p>{currentState.connectionError}</p>
              </Message>
            )}
            
            {testMode && (
              <div style={{ marginTop: '20px', padding: '10px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px' }}>
                <p style={{ color: '#fff', fontSize: '14px', marginBottom: '10px' }}>Test Mode Controls:</p>
                <Button 
                  size="small" 
                  onClick={() => setTestConnectionError('Connection failed - Test error')}
                  style={{ marginRight: '10px' }}
                >
                  Simulate Error
                </Button>
                <Button 
                  size="small" 
                  onClick={() => setTestConnectionError(null)}
                >
                  Clear Error
                </Button>
              </div>
            )}
          </div>
        );

      case 'calibrate':
        return (
          <div className="step-content">
            <div className="step-image">
              {currentState.isCalibrating ? (
                <div className="calibration-animation">
                  <Icon name="spinner" size="massive" className="spinning" style={{ color: '#FFFFFF' }} />
                </div>
              ) : (
                <Icon name="balance scale" size="massive" style={{ color: '#FFFFFF' }} />
              )}
            </div>
            <p className="step-description">
              {t('setup.bluetooth.calibrate_description')}
            </p>
            
            {currentState.isCalibrating && (
              <div className="calibration-progress">
                <Progress 
                  percent={calibrationProgress} 
                  progress 
                  color="red"
                  className="calibration-progress-bar"
                />
                <p className="progress-text">
                  Collecting data points... {calibrationDataPoints}/{totalDataPoints} ({Math.round(calibrationProgress)}%)
                </p>
                <p className="progress-instruction">
                  Please hold still and maintain a stable position during calibration.
                </p>
                {calibrationComplete && (
                  <div className="calibration-complete">
                    <Icon name="check circle" size="massive" color="green" />
                    <p className="success-message">Calibration successful!</p>
                    <p className="success-description">Your device is now calibrated and ready for your workout.</p>
                    <Button 
                      primary 
                      size="large"
                      onClick={handleStartWorkout}
                      className="step-button success-button"
                    >
                      <Icon name="play" />
                      Start Workout
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {!currentState.isCalibrating && !calibrationComplete && (
              <Button 
                primary 
                size="large"
                onClick={() => {
                  console.log('BluetoothSetupFlow: Calibrate button clicked!');
                  handleStartCalibration();
                }}
                className="step-button"
              >
                <Icon name="play" />
                {t('setup.bluetooth.start_calibration')}
              </Button>
            )}
            
            {currentState.calibrationError && (
              <Message negative className="error-message">
                <Message.Header>{t('setup.bluetooth.calibration_error')}</Message.Header>
                <p>{currentState.calibrationError}</p>
              </Message>
            )}
            
            {testMode && (
              <div style={{ marginTop: '20px', padding: '10px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px' }}>
                <p style={{ color: '#fff', fontSize: '14px', marginBottom: '10px' }}>Test Mode Controls:</p>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ color: '#fff', fontSize: '12px', marginRight: '10px' }}>User Weight:</label>
                  <input 
                    type="number" 
                    value={testUserWeight} 
                    onChange={(e) => setTestUserWeight(parseFloat(e.target.value) || 70)}
                    style={{ width: '80px', padding: '4px', marginRight: '10px' }}
                  />
                  <label style={{ color: '#fff', fontSize: '12px', marginRight: '10px' }}>Added Weight:</label>
                  <input 
                    type="number" 
                    value={testAddedWeights} 
                    onChange={(e) => setTestAddedWeights(parseFloat(e.target.value) || 0)}
                    style={{ width: '80px', padding: '4px' }}
                  />
                </div>
                <Button 
                  size="small" 
                  onClick={() => setTestCalibrationError('Calibration failed - Test error')}
                  style={{ marginRight: '10px' }}
                >
                  Simulate Error
                </Button>
                <Button 
                  size="small" 
                  onClick={() => setTestCalibrationError(null)}
                >
                  Clear Error
                </Button>
              </div>
            )}
          </div>
        );

      case 'ready':
        return (
          <div className="step-content">
            <div className="step-image">
              <Icon name="check circle" size="massive" color="green" />
            </div>
            <Message positive className="success-message">
              <Message.Header>Calibration Successful!</Message.Header>
              <p>Your device is now calibrated and ready for your workout.</p>
              <p><strong>Calibrated Weight:</strong> {currentState.userWeight}kg</p>
              {currentState.addedWeights > 0 && (
                <p><strong>Added Weight:</strong> {currentState.addedWeights}kg</p>
              )}
            </Message>
            <Button 
              primary 
              size="large"
              onClick={handleStartWorkout}
              className="step-button success-button"
            >
              <Icon name="play" />
              {readyButtonText}
            </Button>
          </div>
        );

      case 'complete':
        return (
          <div className="step-content">
            <div className="step-image">
              <Icon name="check circle" size="massive" color="green" />
            </div>
            <Message positive className="success-message">
              <Message.Header>Workout Started!</Message.Header>
              <p>Weight: {currentState.userWeight}kg {currentState.addedWeights > 0 && `+ ${currentState.addedWeights}kg`}</p>
              <p>Your workout is now active. Use the controls below to manage your session.</p>
            </Message>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bluetooth-setup-container">
      {testMode && (
        <div style={{ 
          position: 'fixed', 
          top: '10px', 
          left: '10px', 
          backgroundColor: '#ff6b35', 
          color: 'white', 
          padding: '8px 12px', 
          borderRadius: '4px', 
          fontSize: '12px', 
          fontWeight: 'bold',
          zIndex: 1000
        }}>
          TEST MODE
        </div>
      )}
      <div className="setup-content">
        <Header as="h2" textAlign="center" className="main-header">
          {t('setup.bluetooth.header')}
        </Header>
        
        <div className="step-indicator-container">
          <div className={`step-dot ${currentStep === 'connect' ? 'active' : currentState.isConnected ? 'completed' : ''}`}></div>
          <div className={`step-dot ${currentStep === 'calibrate' ? 'active' : currentState.isCalibrated ? 'completed' : ''}`}></div>
          <div className={`step-dot ${currentStep === 'ready' ? 'active' : isWorkoutStarted ? 'completed' : ''}`}></div>
        </div>

        {renderStepContent()}
      </div>

      {/* Calibration Toast Notifications */}
      <CalibrationToast
        isVisible={toastVisible}
        type={toastType}
        title={toastTitle}
        message={toastMessage}
        onClose={hideToast}
        duration={6000}
      />

    </div>
  );
};

export default withTranslation('workout')(BluetoothSetupFlow);
